import { queryClient } from "App";
import { get } from "lodash";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getEmailSignatureTableColumns,
  getEmailSignatureTableFilters,
} from "routes/email/components/EmailSignatureTableColumnsAndFilters";
import { EMAIL_SIGNATURE_TABLE_CONSTANTS } from "routes/email/EmailSignatureTableV3";
import TagService from "services/Tag";
import UserService from "services/User";
import { addResizedColumns } from "hooks/useTableConfig";
import tagConstants from "constants/Tag";

export const invalidateEmailSignatureTableQuery = async () => {
  await queryClient.invalidateQueries({
    queryKey: [EMAIL_SIGNATURE_TABLE_CONSTANTS.queryKey],
  });
};

const fetchTags = async className => {
  const taggableModels = tagConstants.taggableModels;
  const model = Object.keys(taggableModels).find(
    key => taggableModels[key] === className
  );

  const tags = await TagService.getTags({ model: model, for_filtering: true });
  return tags;
};

export const useEmailSignatureTable = ({
  className = null,
  tableName = null,
}) => {
  const { data: tags, isSuccess: isTagsFetched } = useQuery({
    queryKey: ["tags", className],
    queryFn: () => fetchTags(className),
  });

  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const filters = useMemo(() => {
    if (!isTagsFetched) {
      return [];
    }
    const finalFilters = getEmailSignatureTableFilters({
      tags,
    });

    return finalFilters;
  }, [tags, isTagsFetched]);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getEmailSignatureTableColumns();

    const emailSignatureTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${tableName}`,
      {}
    );

    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: emailSignatureTableSettings?.resized_columns || {},
    });

    return withResizedColumns;
  }, [tableName, tableSettings, isTableSettingsFetched]);

  return { filters, columns, tags };
};
